import {
  Stack,
  Heading,
  Skeleton,
  useToast,
  useResponsiveValue,
  Box,
  Text,
  Inline,
  IconShieldCheck
} from '@machobear/design-system'
import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useParams, useHistory } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { isEmpty } from 'ramda'
import { add } from '@/models/plans/apis'
import useSearchParams from '@/hooks/use-search-params'
import { usePaymentValidation, PaymentFormValuesType } from '@/hooks/use-payment-validation'
import PaymentFormComponent from '@/components/ds-checkout-modal/payment-form'

import { useGetPaymentMethod } from '@/hooks/use-block-and-pay'
import { AddingPaymentMethod } from '@/components/ds-checkout-modal/types'

import StripeLogo from './stripe-icon.svg'

import PaymentSummary from './payment-summary'
import PaymentMethod from './payment-method'
import { FormBox } from '../form-sections'
import FormFooter from '../form-footer'
import { useFormContext } from '../form-context'
import FormWrapper from '../form-wrapper'

const PaymentForm = ({ refetch }: { refetch: () => void }) => {
  const { t } = useTranslation()
  const { couponCode: coupon, setTemporaryPaymentMethod, temporaryPaymentMethod } = useFormContext()
  const { trustScoreId = '' } = useParams<{ trustScoreId: string }>()
  const history = useHistory()
  const {
    data: paymentMethod,
    isLoading,
    isFetching,
    isRefetching,
    refetch: refetchPaymentMethod
  } = useGetPaymentMethod('paymentMethod')

  const showToast = useToast()
  const hasPaymentMethod = paymentMethod && !isEmpty(paymentMethod)

  const state = !isEmpty(paymentMethod)
    ? paymentMethod?.[0].state
    : !isEmpty(temporaryPaymentMethod)
      ? temporaryPaymentMethod?.[0].state
      : null

  const { couponCode = '' }: { couponCode?: string } = useSearchParams()

  const queryClient = useQueryClient()

  const formValidationSchema = usePaymentValidation()
  const validationSchema = hasPaymentMethod ? Yup.object().shape({}) : formValidationSchema

  const showSkeleton = isLoading || isFetching || isRefetching

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values)
  })

  const { mutate, isPending: isAddingPaymentMethod } = addMethod

  const addPaymentMethod = async (values: AddingPaymentMethod) => {
    mutate(
      {
        ...values,
        card_number: values.card_number.replace(/\s/g, ''),
        expiration_date: values.expiration_date.replace('/', '')
      },
      {
        onSuccess: async (result, options) => {
          !options.save_payment_method && setTemporaryPaymentMethod(result)
          history.push(`/trust-score/edit/${trustScoreId}/confirmation`)
          refetch()
        },
        onError: (response: { message: string }) => {
          const errorMessage = response?.message ?? ''
          showToast({
            key: `payment-method-error`,
            message: errorMessage,
            tone: 'critical'
          })
        }
      }
    )
  }

  const handlePaymentSubmit = async (values: PaymentFormValuesType) => {
    if (hasPaymentMethod) return history.push(`/trust-score/edit/${trustScoreId}/confirmation`)
    await addPaymentMethod({ ...values, card_number: values.card_number.replace(/\s/g, '') })
    if (values.save_payment_method) {
      setTemporaryPaymentMethod([])
    }
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const initialValues = {
    default_payment_method: true,
    payment_method: 'stripe',
    first_name: temporaryPaymentMethod[0]?.first_name ?? '',
    last_name: temporaryPaymentMethod[0]?.last_name ?? '',
    street1: temporaryPaymentMethod[0]?.street1 ?? '',
    street2: temporaryPaymentMethod[0]?.street2 ?? '',
    city: temporaryPaymentMethod[0]?.city ?? '',
    state: temporaryPaymentMethod[0]?.state ?? '',
    country: 'ca',
    zip: temporaryPaymentMethod[0]?.zip ?? '',
    card_number: temporaryPaymentMethod[0]?.card_number ?? '',
    expiration_date: temporaryPaymentMethod[0]?.expiration_date ?? '',
    security_code: temporaryPaymentMethod[0]?.security_code ?? '',
    save_payment_method: false,
    save_billing_address: false
  }

  return (
    <Stack align="left" space="small">
      {!isMobile && (
        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
          <Heading level="6" weight="strong">
            {t('l.standalone_trustscore.form.basic_payment_title', 'Payment')}
          </Heading>

          <Box alt="stripe-logo" component="img" src={StripeLogo} marginTop="xxxsmall" />
        </Box>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handlePaymentSubmit}
        validateOnMount={true}
      >
        {({ values }) => {
          return (
            <FormWrapper>
              <Form>
                <Stack space="large">
                  <FormBox
                    label={t('l.standalone_trustscore.form_purchase_summary', 'Purchase Summary')}
                    secondaryLabel={
                      isMobile && (
                        <Box
                          alt="stripe-logo"
                          component="img"
                          src={StripeLogo}
                          marginTop="xxxsmall"
                        />
                      )
                    }
                  >
                    <PaymentSummary
                      couponCode={coupon || couponCode}
                      state={state ?? values.state}
                    />
                  </FormBox>

                  <Stack align="left" space="gutter">
                    <FormBox
                      label={t('l.standalone_trustscore.form_payment_method', 'Payment Method')}
                      secondaryLabel={
                        <Inline space={'xxsmall'} alignY="center">
                          <IconShieldCheck tone="critical" size="xsmall" />
                          <Text size="small" weight="medium" tone="tertiary">
                            Secure Encryption
                          </Text>
                        </Inline>
                      }
                    >
                      {showSkeleton ? (
                        <Skeleton.Shape height={84} />
                      ) : hasPaymentMethod ? (
                        <PaymentMethod
                          client={queryClient}
                          card={paymentMethod[0]}
                          refetch={refetchPaymentMethod}
                          state={state ?? values.state}
                          couponCode={coupon || couponCode}
                        />
                      ) : (
                        <PaymentFormComponent />
                      )}
                    </FormBox>
                  </Stack>
                  <FormFooter isSubmitting={isAddingPaymentMethod} isLoading={isLoading} />
                </Stack>
              </Form>
            </FormWrapper>
          )
        }}
      </Formik>
    </Stack>
  )
}

export default PaymentForm
